import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  getChartOfAccountDepartments,
  getChartOfAccountSubDepartments,
  createChartOfAccountDepartment,
  createChartOfAccountSubDepartment,
  updateChartOfAccountsDepartment,
} from '../../utils/api';
import { optionNameKey, normalizeId, subDepartmentParentId } from './coaShared';

/**
 * useCoaTaxonomy — the org's COA departments + sub-departments for the
 * Chart of Accounts page: one fetch (per location, so rows carry
 * locationDisplayOrder), inline "create" handlers for the InlineSelects,
 * and PATCH for the P&L Mapping tab's inline department edits.
 */
export default function useCoaTaxonomy(orgId, scopedLocationId) {
  const [coaDepartments, setCoaDepartments] = useState([]);
  const [coaSubDepartments, setCoaSubDepartments] = useState([]);

  // Departments are fetched per location so each row carries its
  // locationDisplayOrder (NULL = inherit the org-wide order) — the P&L
  // Mapping tab reads them from here instead of fetching its own copy.
  const refetchDepartments = useCallback(async () => {
    if (!orgId) { setCoaDepartments([]); return []; }
    try {
      const rows = await getChartOfAccountDepartments(orgId, scopedLocationId || undefined);
      setCoaDepartments(rows || []);
      return rows || [];
    } catch (err) {
      console.error('Error fetching COA departments:', err);
      return [];
    }
  }, [orgId, scopedLocationId]);

  useEffect(() => { refetchDepartments(); }, [refetchDepartments]);

  useEffect(() => {
    if (!orgId) { setCoaSubDepartments([]); return; }
    getChartOfAccountSubDepartments(orgId)
      .then((s) => setCoaSubDepartments(s || []))
      .catch((err) => { console.error('Error fetching COA sub-departments:', err); setCoaSubDepartments([]); });
  }, [orgId]);

  const upsertCoaDepartment = useCallback((department, fallbackName, fallbackAccountType) => {
    if (!department && !fallbackName) return null;
    const normalized = {
      ...department,
      id: normalizeId(department?.id),
      name: department?.name || fallbackName,
      accountType: department?.accountType ?? fallbackAccountType ?? null,
    };
    const key = optionNameKey(normalized.name);
    setCoaDepartments((prev) => {
      const idx = prev.findIndex((d) => (normalized.id != null && d.id === normalized.id) || optionNameKey(d.name) === key);
      if (idx === -1) return [...prev, normalized];
      return prev.map((d, i) => (i === idx ? { ...d, ...normalized } : d));
    });
    return normalized;
  }, []);

  const upsertCoaSubDepartment = useCallback((subDepartment, fallbackName, departmentId) => {
    if (!subDepartment && !fallbackName) return null;
    const normalized = {
      ...subDepartment,
      id: normalizeId(subDepartment?.id),
      name: subDepartment?.name || fallbackName,
      departmentId: subDepartmentParentId(subDepartment) ?? departmentId,
      chartOfAccountDepartmentId: subDepartmentParentId(subDepartment) ?? departmentId,
    };
    const key = optionNameKey(normalized.name);
    setCoaSubDepartments((prev) => {
      const idx = prev.findIndex((s) =>
        (normalized.id != null && s.id === normalized.id) ||
        (optionNameKey(s.name) === key && subDepartmentParentId(s) === normalized.departmentId)
      );
      if (idx === -1) return [...prev, normalized];
      return prev.map((s, i) => (i === idx ? { ...s, ...normalized } : s));
    });
    return normalized;
  }, []);

  const ensureCoaDepartment = useCallback(async (name, accountType) => {
    const cleanName = String(name || '').trim();
    if (!cleanName) throw new Error('Department name is required.');
    const existing = coaDepartments.find((d) => optionNameKey(d.name) === optionNameKey(cleanName));
    if (existing) return existing;
    try {
      const created = await createChartOfAccountDepartment(orgId, { name: cleanName, accountType: accountType || null });
      const normalized = upsertCoaDepartment(created, cleanName, accountType);
      toast.success(created?._existing ? `Department "${normalized.name}" already exists — reused.` : `Department "${normalized.name}" created.`);
      return normalized;
    } catch (err) {
      console.error('Error creating COA department:', err);
      toast.error(`Failed to create department "${cleanName}". Try again.`);
      throw err;
    }
  }, [coaDepartments, orgId, upsertCoaDepartment]);

  const createAccountGroupOption = useCallback(async (name) => String(name || '').trim(), []);

  const createDepartmentOption = useCallback(async (name, account) => {
    const department = await ensureCoaDepartment(name, account?.accountType);
    return department.name;
  }, [ensureCoaDepartment]);

  const createSubDepartmentOption = useCallback(async (name, account) => {
    const cleanName = String(name || '').trim();
    const parentName = String(account?.department || '').trim();
    if (!parentName) {
      toast.error('Select or create a department before adding a sub-department.');
      throw new Error('A parent department is required before creating a sub-department.');
    }
    const parentDepartment = await ensureCoaDepartment(parentName, account?.accountType);
    const existing = coaSubDepartments.find((s) =>
      optionNameKey(s.name) === optionNameKey(cleanName) && subDepartmentParentId(s) === parentDepartment.id
    );
    if (existing) return existing.name;
    try {
      const created = await createChartOfAccountSubDepartment(orgId, { name: cleanName, chartOfAccountDepartmentId: parentDepartment.id });
      const normalized = upsertCoaSubDepartment(created, cleanName, parentDepartment.id);
      toast.success(`Sub-department "${normalized.name}" created.`);
      return normalized.name;
    } catch (err) {
      console.error('Error creating COA sub-department:', err);
      toast.error(`Failed to create sub-department "${cleanName}". Try again.`);
      throw err;
    }
  }, [coaSubDepartments, orgId, ensureCoaDepartment, upsertCoaSubDepartment]);

  /** Inline department edits on the P&L Mapping tab (name / code / usaliSection / presentationName). */
  const patchDepartment = useCallback(async (deptId, data) => {
    const updated = await updateChartOfAccountsDepartment(deptId, data);
    setCoaDepartments((prev) => prev.map((d) => (normalizeId(d.id) === normalizeId(deptId) ? { ...d, ...(updated || data) } : d)));
    return updated;
  }, []);

  return {
    coaDepartments,
    coaSubDepartments,
    refetchDepartments,
    createAccountGroupOption,
    createDepartmentOption,
    createSubDepartmentOption,
    patchDepartment,
  };
}
